
import React, { useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface CompanyManagementSectionProps {
  registerSectionRef: (id: string, element: HTMLElement | null) => void;
}

const CompanyManagementSection = ({ registerSectionRef }: CompanyManagementSectionProps) => {
  // Create refs for each section
  const overviewRef = useRef<HTMLHeadingElement>(null);
  const creatingCompanyRef = useRef<HTMLHeadingElement>(null);
  const companyDetailsRef = useRef<HTMLHeadingElement>(null);
  const companyMembersRef = useRef<HTMLHeadingElement>(null);
  const companySwitchingRef = useRef<HTMLHeadingElement>(null);
  const webSearchRef = useRef<HTMLHeadingElement>(null);
  const accessRequestsRef = useRef<HTMLHeadingElement>(null);
  
  // Register all section refs when component mounts or refs change
  useEffect(() => {
    registerSectionRef('companies-overview', overviewRef.current);
    registerSectionRef('creating-company', creatingCompanyRef.current);
    registerSectionRef('company-details', companyDetailsRef.current);
    registerSectionRef('company-members', companyMembersRef.current);
    registerSectionRef('company-switching', companySwitchingRef.current);
    registerSectionRef('company-web-search', webSearchRef.current);
    registerSectionRef('company-access-requests', accessRequestsRef.current);
  }, [registerSectionRef]);

  return (
    <>
      <h2 
        id="companies-overview" 
        ref={overviewRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16"
      >
        Company Management Overview
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Managing Companies</CardTitle>
          <CardDescription>How to work with companies in Discovery Hub</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            The Companies section allows you to manage the organizations you work with or for. In Discovery Hub, 
            companies serve as containers for leads and users, enabling team-based collaboration and organization.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Company Features</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Multiple Companies:</strong> Users can belong to multiple companies</li>
              <li><strong>Company Switching:</strong> Easily switch context between companies</li>
              <li><strong>Member Management:</strong> Add and manage team members within a company</li>
              <li><strong>Company-Specific Leads:</strong> Leads are associated with specific companies</li>
              <li><strong>Web Search Enrichment:</strong> AI-powered company information gathering</li>
              <li><strong>Access Management:</strong> Request and manage access to companies</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Access Control</h4>
            <p>
              Companies in Discovery Hub are protected by a role-based access system. Only users with appropriate permissions
              can view or modify company data. If you need access to a company, you can submit an access request that will be
              reviewed by company administrators.
            </p>
          </div>
        </CardContent>
      </Card>

      <h2 
        id="creating-company" 
        ref={creatingCompanyRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Creating a Company
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Setting Up a New Company</CardTitle>
          <CardDescription>Step-by-step guide to creating a company</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-semibold">Create a New Company</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to the Companies section</li>
              <li>Click the "Add Company" button</li>
              <li>Fill out the company information form:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Company name (required)</li>
                  <li>Industry</li>
                  <li>Size</li>
                  <li>Website</li>
                  <li>Description</li>
                </ul>
              </li>
              <li>Click "Create Company" to finalize</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">After Creation</h4>
            <p>
              Once created, you'll automatically become a company admin and the company will be set as your active company.
              You can then begin adding team members and leads to the company.
            </p>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Who Can Create Companies</h4>
            <p>
              Any authenticated user can create a company. Creating a company automatically makes you an admin of that company.
            </p>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="company-details" 
        ref={companyDetailsRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Company Details
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Viewing and Editing Company Details</CardTitle>
          <CardDescription>Managing company information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            The company details page provides comprehensive information about a company and allows authorized users to make updates.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Company Details Tabs</h4>
            <p>The company details page is organized into multiple tabs:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Details:</strong> Basic company information and web search data</li>
              <li><strong>Associated Leads:</strong> A list of all leads connected to this company</li>
              <li><strong>Members:</strong> Users who have access to this company and their roles</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Editing Company Details</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to the company details page</li>
              <li>Click the "Edit" button (available to admins only)</li>
              <li>Update the necessary information</li>
              <li>Click "Save Changes" to apply the updates</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Quick Actions</h4>
            <p>The company details page also offers quick actions in the sidebar, such as:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Setting a company as your active company</li>
              <li>Adding new leads to the company</li>
              <li>Editing company information</li>
              <li>Deleting the company (admin only)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="company-members" 
        ref={companyMembersRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Managing Company Members
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Team Member Management</CardTitle>
          <CardDescription>Adding and managing users in a company</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub allows company admins to manage team members, controlling who has access to leads and what actions they can perform.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Adding Members</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to the company details page</li>
              <li>Select the "Members" tab</li>
              <li>Click "Add Member" button</li>
              <li>Enter the user's email address</li>
              <li>Select the appropriate role for the user</li>
              <li>Click "Add" to send an invitation</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Company Roles</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Admin:</strong> Full control over the company, including member management, editing settings, and all lead operations</li>
              <li><strong>Member:</strong> Can view and manage leads, cannot manage company settings or members</li>
              <li><strong>Viewer:</strong> Read-only access to company information and leads</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Managing Existing Members</h4>
            <p>
              As a company admin, you can:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Edit a member's role by selecting the edit option in the members table</li>
              <li>Remove a member from the company using the remove option</li>
              <li>View all current members and their roles in the Members tab</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Access Requests</h4>
            <p>
              Users can request access to a company if they don't currently have access. Company admins will receive notifications about these requests and can approve or deny them as needed.
            </p>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="company-switching" 
        ref={companySwitchingRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Company Switching
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Working with Multiple Companies</CardTitle>
          <CardDescription>How to switch between different companies</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub allows users to belong to multiple companies and easily switch between them. When you switch your active company,
            the system will display leads and data relevant to that company.
          </p>
          
          <div className="space-y-2">
            <h4 className="font-semibold">Using the Company Switcher</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Locate the Company Switcher in the header or navigation area</li>
              <li>Click on the dropdown showing your current active company</li>
              <li>Select a different company from the list</li>
              <li>The system will refresh to show data for the newly selected company</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Setting Active Company from Company Details</h4>
            <p>
              You can also set a company as your active company directly from its details page:
            </p>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to the company details page</li>
              <li>Click the "Set as Active Company" button in the sidebar</li>
              <li>The system will update your active company context</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Active Company Context</h4>
            <p>
              Your active company determines:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Which leads are displayed by default in the Leads section</li>
              <li>Which company context is used when creating new leads</li>
              <li>The primary focus for dashboards and analytics</li>
            </ul>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="company-web-search" 
        ref={webSearchRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Company Web Search Enrichment
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>AI-Powered Company Research</CardTitle>
          <CardDescription>Automatically gather information about companies</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub includes a powerful web search feature that automatically researches companies 
            and enriches their profiles with detailed information gathered from across the internet.
          </p>
          
          <div className="space-y-2">
            <h4 className="font-semibold">Web Search Features</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Company Summary:</strong> An AI-generated overview of the company</li>
              <li><strong>Industry Insights:</strong> Information about the company's industry position</li>
              <li><strong>Products and Services:</strong> Lists of what the company offers</li>
              <li><strong>Key People:</strong> Information about company leadership</li>
              <li><strong>Competitors:</strong> Identification of market competitors</li>
              <li><strong>Recent News:</strong> Latest news about the company</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Starting a Web Search</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to a company's details page</li>
              <li>Find the Web Search panel in the Details tab</li>
              <li>Click "Start Web Search" if no search has been run yet</li>
              <li>Wait for the search to complete (this may take several minutes)</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Generated Content</h4>
            <p>
              The web search feature can also generate useful content:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Email Templates:</strong> Customized email templates for contacting the company</li>
              <li><strong>Call Scripts:</strong> Guided talking points for phone conversations</li>
            </ul>
            <p className="mt-2">
              These templates are based on the research data and can be accessed from the Summary tab of the Web Search panel.
            </p>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Refreshing Web Search Data</h4>
            <p>
              To update the web search information:
            </p>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Go to the Web Search panel in the company details</li>
              <li>Click the refresh button in the panel header</li>
              <li>The system will queue a new web search for the company</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <h2 
        id="company-access-requests" 
        ref={accessRequestsRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Company Access Requests
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Requesting and Managing Access</CardTitle>
          <CardDescription>How to request and manage company access</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Users can request access to companies they don't currently have access to, and company 
            administrators can manage these requests.
          </p>
          
          <div className="space-y-2">
            <h4 className="font-semibold">Requesting Access</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to the Companies section</li>
              <li>Search for the company you want to access</li>
              <li>Click on the company to view its limited details</li>
              <li>Click the "Request Access" button</li>
              <li>Add any relevant notes explaining why you need access</li>
              <li>Submit your request</li>
            </ol>
            <p className="mt-2">
              Your request will be sent to the company administrators, who can approve or deny it.
            </p>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Approving Access Requests (Admins)</h4>
            <p>
              As a company admin, you'll receive notifications when users request access to your company:
            </p>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Go to the company details page</li>
              <li>Navigate to the Members tab</li>
              <li>Look for the "Pending Requests" section</li>
              <li>Review each request and the requestor's notes</li>
              <li>Click "Approve" to grant access or "Deny" to reject the request</li>
              <li>When approving, select the appropriate role for the user</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Access Notification</h4>
            <p>
              Users will receive notifications when their access requests are approved or denied.
              If approved, they'll immediately gain access to the company with the assigned role.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default CompanyManagementSection;
